/**
 * MapGallery — real preview images per map, full-screen viewer,
 * per-map download (PNG), and one-click regenerate. Sprint P37y · Sprint C.
 */
import { useMemo, useState, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  MapTrifold, DownloadSimple, ArrowsOut, X, Sparkle, ArrowClockwise,
  Info, Warning, Archive, Play, ArrowSquareOut,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

// Cache blob URLs per map so we don't re-fetch on every render.
function useBlobUrl(pid, mapId, deps = []) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!mapId) return;
    let cancelled = false;
    let objUrl = null;
    (async () => {
      try {
        const r = await api.get(`/projects/${pid}/gis/gallery/${mapId}.png`, {
          responseType: "blob",
        });
        if (cancelled) return;
        objUrl = URL.createObjectURL(r.data);
        setUrl(objUrl);
      } catch { /* silent */ }
    })();
    return () => {
      cancelled = true;
      if (objUrl) URL.revokeObjectURL(objUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pid, mapId, ...deps]);
  return url;
}

function MapThumb({ pid, mapId, alt, generatedAt, testId }) {
  const url = useBlobUrl(pid, mapId, [generatedAt]);
  return url ? (
    <img src={url} alt={alt} loading="lazy" data-testid={testId}
              className="w-full h-full object-cover" />
  ) : (
    <div className="w-full h-full flex items-center justify-center text-slate-400 text-[10.5px] bg-slate-100">
      <ArrowClockwise size={14} className="animate-spin mr-1" /> Loading…
    </div>
  );
}

function MapFullImage({ pid, mapId, alt, generatedAt }) {
  const url = useBlobUrl(pid, mapId, [generatedAt]);
  return url ? (
    <img src={url} alt={alt} data-testid="gallery-modal-image"
              className="max-w-full max-h-full object-contain" />
  ) : (
    <div className="text-slate-400 text-[12px]"><ArrowClockwise size={14} className="animate-spin inline mr-1" /> Loading full-resolution image…</div>
  );
}

async function downloadMap(pid, mapId, filename) {
  try {
    const r = await api.get(`/projects/${pid}/gis/gallery/${mapId}.png`, {
      responseType: "blob",
    });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = filename || `${mapId}.png`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  } catch (e) {
    toast.error("Download failed");
  }
}

export default function MapGallery({ pid, boundary }) {
  const [selected, setSelected] = useState(null);
  const [catFilter, setCatFilter] = useState("all");

  const galleryQ = useQuery({
    queryKey: ["gis-gallery", pid],
    queryFn: () => api.get(`/projects/${pid}/gis/gallery`).then((r) => r.data),
    refetchInterval: 15_000,
  });
  const paragraphsQ = useQuery({
    queryKey: ["gis-map-narratives", pid],
    queryFn: () => api.get(`/projects/${pid}/narratives?tag=gis_map&limit=50`).then((r) => r.data).catch(() => ({ paragraphs: [] })),
  });

  const generateMut = useMutation({
    mutationFn: (force) =>
      api.post(`/projects/${pid}/gis/gallery/generate${force ? "?force=true" : ""}`).then((r) => r.data),
    onSuccess: (r) => {
      toast.success(`${r.count || 0} maps rendered`);
      galleryQ.refetch();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Generation failed"),
  });

  const maps = useMemo(() => galleryQ.data?.maps || [], [galleryQ.data]);
  const paragraphs = useMemo(() => paragraphsQ.data?.paragraphs || [], [paragraphsQ.data]);
  const narrativeByMap = useMemo(() => {
    const m = {};
    paragraphs.forEach((p) => { if (p.map_id) m[p.map_id] = p; });
    return m;
  }, [paragraphs]);
  const categories = useMemo(() => {
    const set = new Set(maps.map((m) => m.category || "Other"));
    return ["all", ...Array.from(set).sort()];
  }, [maps]);
  const filtered = useMemo(() =>
    catFilter === "all" ? maps : maps.filter((m) => (m.category || "Other") === catFilter),
    [maps, catFilter]);

  return (
    <div className="space-y-4" data-testid="map-gallery-panel">
      {/* Header controls */}
      <div className="rounded-lg bg-white border border-slate-200 p-4 flex items-start gap-3">
        <MapTrifold size={16} className="text-brand-blue mt-1" weight="duotone" />
        <div className="flex-1 min-w-0">
          <h2 className="font-heading font-bold text-[15px] text-slate-900">GIS Map Gallery</h2>
          <p className="text-[11.5px] text-slate-500">
            {maps.length > 0
              ? `${maps.length} maps rendered${galleryQ.data?.generated_at ? " · " + new Date(galleryQ.data.generated_at).toLocaleString() : ""}`
              : "No maps rendered yet. Click Generate to build them from the approved boundary + collars."}
          </p>
        </div>
        {boundary?.filename && (
          <span className="inline-flex items-center gap-1 rounded-sm border border-brand-blue/30 bg-brand-blue/5 text-brand-blue px-2 py-0.5 text-[10.5px] uppercase tracking-widest shrink-0"
                   data-testid="gallery-boundary-chip">
            <MapTrifold size={10} weight="fill" /> {boundary.filename}{boundary.area_km2 ? ` · ${Number(boundary.area_km2).toFixed(2)} km²` : ""}
          </span>
        )}
        <div className="flex items-center gap-2 shrink-0">
          <button type="button" onClick={() => generateMut.mutate(false)}
                     disabled={generateMut.isPending}
                     data-testid="btn-generate-gallery"
                     className="inline-flex items-center gap-1 rounded-md bg-brand-blue hover:bg-brand-blue/90 text-white px-3 h-9 text-[11.5px] disabled:opacity-50">
            {generateMut.isPending ? <ArrowClockwise size={12} className="animate-spin" /> : <Play size={12} weight="fill" />}
            {maps.length ? "Refresh" : "Generate Maps"}
          </button>
          <button type="button" onClick={() => generateMut.mutate(true)}
                     disabled={generateMut.isPending}
                     data-testid="btn-regenerate-gallery"
                     className="inline-flex items-center gap-1 rounded-md border border-slate-200 hover:bg-slate-50 px-2.5 h-9 text-[11px] text-slate-700 disabled:opacity-50">
            <ArrowClockwise size={11} /> Force Rebuild
          </button>
          {maps.length > 0 && (
            <button type="button" onClick={async () => {
                          try {
                            const r = await api.get(`/projects/${pid}/gis/gallery.zip`, { responseType: "blob" });
                            const url = URL.createObjectURL(r.data);
                            const a = document.createElement("a");
                            a.href = url; a.download = `gis_maps_${pid.slice(0, 8)}.zip`;
                            document.body.appendChild(a); a.click(); a.remove();
                            setTimeout(() => URL.revokeObjectURL(url), 5000);
                            toast.success("ZIP downloaded");
                          } catch { toast.error("ZIP download failed"); }
                        }}
                        data-testid="btn-download-gallery-zip"
                        className="inline-flex items-center gap-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white px-2.5 h-9 text-[11px]">
              <Archive size={11} weight="fill" /> ZIP
            </button>
          )}
        </div>
      </div>

      {/* Category filter pills */}
      {maps.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="gallery-category-filters">
          {categories.map((c) => (
            <button key={c} type="button"
                        onClick={() => setCatFilter(c)}
                        data-testid={`gallery-filter-${c}`}
                        className={`inline-flex items-center gap-1 rounded-sm border px-2 py-0.5 text-[10.5px] uppercase tracking-widest ${
                          catFilter === c ? "bg-brand-blue text-white border-brand-blue" : "bg-white text-slate-700 border-slate-200 hover:border-brand-blue/40"
                        }`}>
              {c === "all" ? `All (${maps.length})` : `${c} (${maps.filter((m) => (m.category || "Other") === c).length})`}
            </button>
          ))}
        </div>
      )}

      {/* Empty state */}
      {maps.length === 0 && !generateMut.isPending && (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50/60 p-8 text-center">
          <MapTrifold size={22} className="text-slate-400 mx-auto mb-2" weight="duotone" />
          <div className="text-[13px] text-slate-700 font-semibold">No maps in the gallery yet</div>
          <div className="text-[11.5px] text-slate-500 mt-1">
            Click <b>Generate Maps</b> above. Maps will render from the approved project boundary,
            with drillhole overlays when collar data is available.
          </div>
        </div>
      )}
      {generateMut.isPending && maps.length === 0 && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-8 text-center">
          <ArrowClockwise size={18} className="text-brand-blue mx-auto animate-spin mb-2" />
          <div className="text-[13px] text-slate-700 font-semibold">Rendering maps…</div>
          <div className="text-[11px] text-slate-500 mt-1">This may take up to a minute for the full 18-map bundle.</div>
        </div>
      )}

      {/* Preview grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="gallery-grid">
        {filtered.map((m) => {
          const paragraph = narrativeByMap[m.map_id];
          return (
            <div key={m.map_id}
                    className="group rounded-lg border border-slate-200 bg-white overflow-hidden hover:border-brand-blue/40 hover:shadow-md transition"
                    data-testid={`gallery-card-${m.map_id}`}>
              <button type="button" onClick={() => setSelected({ ...m, paragraph })}
                          className="block w-full text-left">
                <div className="relative bg-slate-100 aspect-[4/3] overflow-hidden">
                  <MapThumb pid={pid} mapId={m.map_id} alt={m.title}
                                 generatedAt={m.generated_at}
                                 testId={`gallery-img-${m.map_id}`} />
                  <div className="absolute top-1 left-1 inline-flex items-center gap-1 rounded-sm bg-white/85 text-slate-700 border border-white px-1.5 py-0.5 text-[9.5px] uppercase tracking-widest">
                    {m.category}
                  </div>
                  {paragraph && (
                    <div className="absolute top-1 right-1 inline-flex items-center gap-0.5 rounded-sm bg-brand-blue/95 text-white px-1 py-0.5 text-[9.5px]">
                      <Sparkle size={9} weight="fill" /> AI
                    </div>
                  )}
                </div>
                <div className="p-2.5">
                  <div className="text-[12.5px] font-semibold text-slate-900 truncate">{m.title}</div>
                  <div className="text-[10.5px] text-slate-500 font-mono">{m.map_id} · {Math.round(m.size_bytes / 1024)} KB</div>
                </div>
              </button>
              <div className="flex items-center gap-1 px-2 py-1.5 border-t border-slate-100">
                <button type="button" onClick={() => setSelected({ ...m, paragraph })}
                            data-testid={`gallery-view-${m.map_id}`}
                            className="inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[10.5px] text-slate-700 hover:bg-slate-100">
                  <ArrowsOut size={10} /> View
                </button>
                <a href="#" onClick={(e) => { e.preventDefault(); downloadMap(pid, m.map_id, m.filename); }}
                     data-testid={`gallery-download-${m.map_id}`}
                     className="inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[10.5px] text-brand-blue hover:bg-brand-blue/5">
                  <DownloadSimple size={10} /> PNG
                </a>
              </div>
            </div>
          );
        })}
      </div>

      {/* Full-screen preview modal */}
      {selected && (
        <div className="fixed inset-0 z-40 bg-slate-900/85 flex items-center justify-center p-4"
                data-testid="gallery-modal">
          <div className="max-w-6xl w-full max-h-full bg-white rounded-lg overflow-hidden flex flex-col">
            <header className="px-4 py-3 border-b border-slate-200 flex items-center gap-3">
              <MapTrifold size={16} className="text-brand-blue" weight="duotone" />
              <div className="flex-1 min-w-0">
                <div className="font-heading font-bold text-[14px] text-slate-900 truncate">{selected.title}</div>
                <div className="text-[10.5px] text-slate-500 font-mono">
                  {selected.map_id} · {selected.category} · {Math.round(selected.size_bytes / 1024)} KB
                </div>
              </div>
              <a href="#" onClick={(e) => { e.preventDefault(); downloadMap(pid, selected.map_id, selected.filename); }}
                   className="inline-flex items-center gap-1 rounded-md bg-brand-blue hover:bg-brand-blue/90 text-white px-2.5 h-8 text-[11.5px]"
                   data-testid="gallery-modal-download">
                <DownloadSimple size={11} weight="fill" /> Download PNG
              </a>
              <button onClick={() => setSelected(null)}
                          data-testid="gallery-modal-close"
                          className="p-1 hover:bg-slate-100 rounded"><X size={14} /></button>
            </header>
            <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1fr_320px] overflow-hidden">
              <div className="bg-slate-50 overflow-auto flex items-center justify-center p-2">
                <MapFullImage pid={pid} mapId={selected.map_id}
                                    alt={selected.title}
                                    generatedAt={selected.generated_at} />
              </div>
              <aside className="border-l border-slate-200 p-4 overflow-y-auto space-y-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Category</div>
                  <div className="text-[12.5px] text-slate-800">{selected.category}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Generated</div>
                  <div className="text-[12px] text-slate-800 font-mono">{selected.generated_at ? new Date(selected.generated_at).toLocaleString() : "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Checksum</div>
                  <div className="text-[10px] text-slate-600 font-mono break-all">{(selected.sha256 || "").slice(0, 24)}…</div>
                </div>
                <div className="pt-3 border-t border-slate-100">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 flex items-center gap-1">
                    <Sparkle size={9} weight="fill" className="text-brand-blue" />
                    AI Interpretation
                  </div>
                  {selected.paragraph ? (
                    <div className="text-[12px] text-slate-700 whitespace-pre-line" data-testid="gallery-modal-narrative">
                      {selected.paragraph.text}
                    </div>
                  ) : (
                    <div className="text-[11px] text-slate-500 italic">
                      No AI interpretation has been generated for this map yet.
                    </div>
                  )}
                </div>
              </aside>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
